import { RefreshError } from './RefreshError';
import { LoadingIndicator } from './LoadingIndicator';
import { useCallback, useMemo, useEffect, useState } from 'react';
import { Link } from 'react-router';
import type {
  CellMouseDownEvent,
  ColDef,
  GetRowIdParams,
  RowClickedEvent,
} from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { AllCommunityModule, ModuleRegistry } from 'ag-grid-community';
import TimeAgo from 'react-timeago';
import { useJobs } from '../hooks/useJobs';
import type {
  JobListItem,
  JobsFilterState,
  JobsScope,
  JobStatusCounts,
} from '../types/jobs';
import { type JobStatus, isTerminal } from '../types/monitoring';
import {
  admissionDiagnostics,
  dotClassForStatus,
  labelForStatus,
  textClassForStatus,
  isLongRunning,
} from '../utils/jobStatus';
import { ErrorDisplay } from './ErrorDisplay';
import { useAuthContext } from '../contexts/AuthContext';
import { useStopRun } from '../hooks/useStopRun';
import { CopyButton } from './ag-grid/cellRenderers';
import { isButtonClick } from './ag-grid/isButtonClick';
import { StopRunDialog } from './StopRunDialog';
import { isCopyButtonClick } from './ag-grid/isCopyButtonClick';
import { isLinkClick } from './ag-grid/isLinkClick';
import { PageHeader } from './PageHeader';
import { useViewers, type UseViewersResult } from '../hooks/useViewers';
import { openViewerUrl, viewerLinkTarget } from '../utils/viewers';
import { OpenInViewerMenu } from './OpenInMenu';
import { ListSkeleton } from './ListSkeleton';
import { Pagination } from './Pagination';
import { useClampPage } from '../hooks/useClampPage';
import { useGridLayout } from '../hooks/useGridLayout';
import { ResetGridLayoutButton } from './ag-grid/ResetGridLayoutButton';
import './ag-grid/styles.css';

ModuleRegistry.registerModules([AllCommunityModule]);

const PAGE_SIZE = 50;
// Two-line identity cells (id over owner).
const ROW_HEIGHT = 52;

// Shared with the grid via AgGrid's `context`, so the stop cell can know the
// caller (owner-only control) and hand the row to the list's one dialog.
interface JobsGridContext {
  callerSub: string | null;
  requestStop: (job: JobListItem) => void;
}

// Status order for the filter chips — active states first so a user scanning
// the bar sees in-flight work before terminal piles.
const STATUS_ORDER: JobStatus[] = [
  'running',
  'waiting_for_capacity',
  'stuck',
  'pending',
  'importing',
  'failed',
  'complete',
  'deleted',
];

function StatusBadge({
  status,
  admission,
}: Pick<JobListItem, 'status' | 'admission'>) {
  if (status == null) return <span className="text-muted">unknown</span>;
  return (
    <span
      className={`inline-flex items-center gap-1.5 whitespace-nowrap text-[12px] font-medium ${textClassForStatus(status)}`}
      title={
        status === 'deleted'
          ? 'This job was explicitly deleted; deletion takes precedence over prior results.'
          : undefined
      }
    >
      <i className={dotClassForStatus(status)} aria-hidden="true" />
      {labelForStatus(status, admission)}
    </span>
  );
}

function ScopeToggle({
  scope,
  onChange,
}: {
  scope: JobsScope;
  onChange: (next: JobsScope) => void;
}) {
  const options: { value: JobsScope; label: string }[] = [
    { value: 'mine', label: 'Mine' },
    { value: 'all', label: 'All' },
  ];
  return (
    <div className="seg" role="group" aria-label="Job scope">
      {options.map(opt => {
        const isActive = scope === opt.value;
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => {
              if (!isActive) onChange(opt.value);
            }}
            aria-pressed={isActive}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

function StatusFilterBar({
  counts,
  total,
  stale = false,
  active,
  onSelect,
}: {
  counts: JobStatusCounts;
  total: number;
  /** The counts describe the previous scope or search; the new ones load. */
  stale?: boolean;
  active: JobStatus | null;
  onSelect: (status: JobStatus | null) => void;
}) {
  const present = STATUS_ORDER.filter(s => (counts[s] ?? 0) > 0);
  const chip = (
    key: string,
    label: string,
    count: number,
    isActive: boolean,
    onClick: () => void
  ) => (
    <button
      key={key}
      type="button"
      onClick={onClick}
      aria-pressed={isActive}
      className="inline-flex items-center gap-1.5"
    >
      <span>{label}</span>
      {/* Always rendered, so a chip never changes width while loading. */}
      <span
        className={`font-normal tabular-nums transition-opacity ${stale ? 'opacity-30' : 'opacity-70'}`}
      >
        {count}
      </span>
    </button>
  );

  return (
    <div className="seg flex-wrap" role="group" aria-label="Status filter">
      {chip('all', 'All', total, active === null, () => onSelect(null))}
      {present.map(s =>
        // The rows' own words ("Queued", not "pending"), so a chip names
        // exactly what the rows it selects say.
        chip(s, labelForStatus(s), counts[s] ?? 0, active === s, () =>
          onSelect(active === s ? null : s)
        )
      )}
    </div>
  );
}

export function StatusCell({ data }: { data: JobListItem }) {
  const pending = data.pending_interactions ?? 0;
  // Samples parked on a human approval are the fact that matters to the
  // person reading the list, so they become the status: one amber phrase in
  // the same dot-and-words language, with the run's own status in the tooltip.
  const blocked =
    pending > 0 && data.status != null && !isTerminal(data.status);
  const badge =
    blocked && data.status != null ? (
      <span
        className="inline-flex items-center gap-1.5 whitespace-nowrap text-[12px] font-medium text-warn"
        title={`${labelForStatus(data.status)}; ${pending} sample${pending === 1 ? '' : 's'} parked waiting on a human approval. Click to view status.`}
      >
        <i className="dot dot-live text-warn-mark" aria-hidden="true" />
        {pending} awaiting approval
      </span>
    ) : (
      <StatusBadge status={data.status} admission={data.admission} />
    );
  const admissionText = admissionDiagnostics(data.admission).join(' · ');

  // The badge is the way to the run's status page, where Stop lives: live
  // runs to watch and stop, finished ones to read their final state.
  const linkable = data.job_type === 'eval-set' && data.status !== null;

  const statusNode = linkable ? (
    <Link
      to={`/eval-set/${encodeURIComponent(data.job_id)}/status`}
      onClick={(e: React.MouseEvent) => e.stopPropagation()}
      title="View live status"
      className="no-underline hover:underline"
    >
      {badge}
    </Link>
  ) : (
    badge
  );

  return (
    <span className="flex h-full w-full min-w-0 flex-col items-start justify-center gap-0.5 overflow-hidden">
      <span className="flex max-w-full shrink-0 items-center gap-1.5">
        {statusNode}
      </span>
      {admissionText && (
        <span
          title={admissionText}
          aria-label={`Admission details: ${admissionText}`}
          className="block w-full min-w-0 truncate text-[11px] leading-tight text-muted"
        >
          {admissionText}
        </span>
      )}
    </span>
  );
}

// A run still going after a day is the one worth a second look (forgotten
// jobs keep burning compute): its age goes amber, with the tooltip saying
// why. No label and no row wash; "1 day ago" in amber already says it.
function AgeCell({ data }: { data: JobListItem }) {
  if (!data.created_at) return <span>-</span>;
  const longRunning = isLongRunning(data);
  const started = new Date(data.created_at).toLocaleString();
  return (
    <span
      className={`text-xs tabular-nums ${longRunning ? 'font-medium text-warn' : 'text-muted'}`}
      title={
        longRunning ? `Running for over 24 hours (started ${started})` : started
      }
    >
      <TimeAgo date={data.created_at} />
    </span>
  );
}

/**
 * The row's one control: a quiet round stop glyph on the caller's own
 * in-flight eval sets, opening the shared Stop dialog (Force stop is one click
 * further in there). Nothing on other people's runs or finished ones, gated on
 * the real owner rather than the list scope so a 403 is never invited.
 */
function StopCell({
  data,
  context,
}: {
  data: JobListItem;
  context: JobsGridContext;
}) {
  const eligible =
    data.job_type === 'eval-set' &&
    context.callerSub !== null &&
    data.created_by === context.callerSub &&
    data.status != null &&
    !isTerminal(data.status);
  if (!eligible) return null;
  return (
    <button
      type="button"
      className="btn btn-sm btn-icon text-muted hover:text-err"
      title="Stop…"
      aria-label={`Stop ${data.job_id}`}
      onClick={(e: React.MouseEvent) => {
        e.stopPropagation();
        context.requestStop(data);
      }}
    >
      <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true">
        <rect x="1" y="1" width="8" height="8" rx="1.5" fill="currentColor" />
      </svg>
    </button>
  );
}

function viewHref(
  data: JobListItem,
  primaryUrl: UseViewersResult['primaryUrl']
): string {
  return data.job_type === 'eval-set'
    ? primaryUrl({ level: 'eval_set', evalSetId: data.job_id })
    : `/scan/${encodeURIComponent(data.job_id)}`;
}

// Identity on two lines: the id (the link) with room to read its suffix, and
// beneath it what kind of job it is (only when it is not the usual eval set)
// and who ran it. Scans, the minority, also get a glyph before the id so the
// eye separates them while running down the column.
function NameCell({ data }: { data: JobListItem }) {
  const scan = data.job_type === 'scan';
  const { primaryUrl } = useViewers();
  const href = viewHref(data, primaryUrl);
  return (
    <span className="flex w-full min-w-0 flex-col justify-center leading-tight">
      <span className="copy-button-cell">
        {scan && <ScanGlyph />}
        <a
          href={href}
          {...viewerLinkTarget(href)}
          className="copy-button-cell-text text-sm font-medium text-ink no-underline hover:text-accent hover:underline"
          title={data.job_id}
          onClick={(e: React.MouseEvent) => e.stopPropagation()}
        >
          {data.job_id}
        </a>
        <CopyButton value={data.job_id} />
        {!scan && (
          <OpenInViewerMenu
            target={{ level: 'eval_set', evalSetId: data.job_id }}
            subject={data.job_id}
            className="ml-1"
          />
        )}
      </span>
      <span
        className="truncate text-xs text-muted"
        title={data.created_by || undefined}
      >
        {scan && <span className="text-ink-2">Scout scan · </span>}
        {data.created_by || 'unknown owner'}
      </span>
    </span>
  );
}

// A magnifier: Scout scans read transcripts looking for something.
function ScanGlyph() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      aria-label="Scout scan"
      role="img"
      className="mr-1 shrink-0 text-muted"
    >
      <circle cx="7" cy="7" r="4.25" />
      <path d="M10.2 10.2 13.5 13.5" />
    </svg>
  );
}

interface JobsListProps {
  filters: JobsFilterState;
  setFilters: (partial: Partial<JobsFilterState>) => void;
}

export function JobsList({ filters, setFilters }: JobsListProps) {
  const { user } = useAuthContext();
  const { primaryUrl } = useViewers();
  const callerSub = user?.sub ?? null;

  const {
    jobs,
    isLoading,
    isFetching,
    isPlaceholder,
    hasData,
    error,
    total,
    statusCounts,
    refresh,
    refetch,
  } = useJobs({
    scope: filters.scope,
    page: filters.page,
    limit: PAGE_SIZE,
    search: filters.search,
    status: filters.status,
    waiting: filters.waiting,
  });

  // Skeleton only for the very first load; later refetches (search, filters,
  // the background refresh) keep the grid mounted so scroll and focus stay.
  const hasLoaded = hasData;

  // The API counts statuses over scope and search only, ignoring the status
  // and approval filters, so after a status click the counts on screen are
  // still exact. Only a scope or search change leaves them stale; then they
  // stay in place, dimmed, until the new ones arrive.
  const countsKey = `${filters.scope}\0${filters.search}`;
  const [settled, setSettled] = useState<{
    key: string;
    /** The unfiltered total; jobs with no status count here, not in chips. */
    allTotal: number | null;
  } | null>(null);
  if (hasData && !isPlaceholder) {
    const unfiltered = filters.status === null && !filters.waiting;
    const allTotal = unfiltered
      ? total
      : settled?.key === countsKey
        ? settled.allTotal
        : null;
    if (settled?.key !== countsKey || settled.allTotal !== allTotal) {
      setSettled({ key: countsKey, allTotal });
    }
  }
  const countsStale = isPlaceholder && settled?.key !== countsKey;
  const countedTotal = Object.values(statusCounts).reduce((a, b) => a + b, 0);
  // While a status filter loads, its total is already known from the counts.
  const knownTotal = !isPlaceholder
    ? total
    : countsStale || filters.waiting
      ? null
      : filters.status
        ? (statusCounts[filters.status] ?? 0)
        : (settled?.allTotal ?? null);
  const headerTotal = knownTotal ?? total;
  const headerStale = knownTotal === null;

  // Runs change state on their own; refresh this view quietly while the tab
  // is visible (other filter views refresh when next shown).
  useEffect(() => {
    const id = setInterval(() => {
      if (document.visibilityState === 'visible') refresh();
    }, 30_000);
    return () => clearInterval(id);
  }, [refresh]);

  const handlePageChange = useCallback(
    (newPage: number) => {
      setFilters({ page: newPage });
    },
    [setFilters]
  );

  // One dialog for the whole list; a row's stop glyph names the target.
  const [stopTarget, setStopTarget] = useState<JobListItem | null>(null);
  const onStopped = useCallback(() => {
    setStopTarget(null);
    refetch();
  }, [refetch]);
  const stop = useStopRun(stopTarget?.job_id ?? null, onStopped);
  const gridContext = useMemo<JobsGridContext>(
    () => ({ callerSub, requestStop: setStopTarget }),
    [callerSub]
  );

  // The URL `?page=` can outrun reality if results shrink (job deleted, scope
  // narrowed, search narrowed). Clamp for render math and reconcile the URL
  // once the data has actually arrived so the pagination controls don't lie.
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const displayPage = Math.min(filters.page, totalPages);

  const setPage = useCallback(
    (page: number) => setFilters({ page }),
    [setFilters]
  );
  useClampPage(
    filters.page,
    totalPages,
    hasData && !isLoading && !isPlaceholder && !error,
    setPage
  );

  // "Waiting on approval" is a server-side filter (forwarded to /meta/jobs as
  // `waiting=1`), applied before pagination so `total`, the page slice, and the
  // rows agree. Parked runs are long-lived — their old created_at sorts them
  // onto later pages — so a page-local filter would hide the very runs an
  // on-call (scope=all) is looking for. We render the server rows directly.
  const columnDefs = useMemo<ColDef<JobListItem>[]>(
    () => [
      {
        field: 'job_id',
        headerName: 'Name',
        // A fixed width rather than `flex: 1`: job ids run to about 45
        // characters, and letting the column soak up every spare pixel made
        // it dominate a wide window for no gain. Wide enough for the longest
        // id with its copy button; users can drag it, and useGridLayout
        // remembers where they put it.
        width: 400,
        minWidth: 280,
        cellRenderer: NameCell,
      },
      {
        // Explicit ids on field-less columns, so the persisted layout keeps
        // referring to the same column if these definitions are reordered.
        colId: 'status',
        headerName: 'Status',
        width: 190,
        sortable: false,
        cellRenderer: StatusCell,
      },
      {
        field: 'created_at',
        headerName: 'Age',
        width: 108,
        cellRenderer: AgeCell,
      },
      {
        colId: 'stop',
        headerName: '',
        width: 52,
        sortable: false,
        resizable: false,
        cellClass: 'justify-end',
        cellRenderer: StopCell,
      },
    ],
    []
  );

  const defaultColDef = useMemo<ColDef<JobListItem>>(
    () => ({
      sortable: true,
      resizable: true,
      filter: false,
    }),
    []
  );

  const getRowId = useCallback(
    (params: GetRowIdParams<JobListItem>) => params.data.job_id,
    []
  );

  const layout = useGridLayout('jobs');

  // Clicking a row opens the job, like the name link does. Links and buttons
  // inside the row (stop, copy) keep their own behaviour: the grid's row click
  // is its own event, so their stopPropagation can't reach it, and a stop
  // click must never also open the run.
  const onRowClicked = useCallback(
    (event: RowClickedEvent<JobListItem>) => {
      if (
        !event.data ||
        isLinkClick(event) ||
        isButtonClick(event) ||
        isCopyButtonClick(event)
      ) {
        return;
      }
      // Modifier and middle clicks are handled on mousedown, opening a new tab.
      const mouse = event.event as MouseEvent | undefined;
      if (mouse?.button === 1 || mouse?.ctrlKey || mouse?.metaKey) return;
      openViewerUrl(viewHref(event.data, primaryUrl));
    },
    [primaryUrl]
  );

  const onCellMouseDown = useCallback(
    (event: CellMouseDownEvent<JobListItem>) => {
      const mouse = event.event as MouseEvent;
      if (mouse.button === 1 || mouse.ctrlKey || mouse.metaKey) {
        // The name link already opens itself in a new tab on these clicks.
        if (isLinkClick(event) || isButtonClick(event)) return;
        if (event.data) {
          openViewerUrl(viewHref(event.data, primaryUrl), { newTab: true });
        }
      }
    },
    [primaryUrl]
  );

  if (error && !hasData) {
    return <ErrorDisplay message={error.toString()} />;
  }

  return (
    <div className="pane flex h-full flex-col overflow-hidden">
      <PageHeader
        title="Jobs"
        description="Eval sets and scans you launched, with live status. Open a run to read its transcripts."
        count={
          hasLoaded ? (
            <span
              className={`transition-opacity ${headerStale ? 'opacity-40' : ''}`}
            >
              {headerTotal.toLocaleString()}
            </span>
          ) : undefined
        }
        actions={
          <button
            type="button"
            onClick={refresh}
            disabled={isFetching}
            className="btn btn-ghost"
          >
            Refresh
          </button>
        }
      >
        <ScopeToggle
          scope={filters.scope}
          onChange={next => setFilters({ scope: next })}
        />
        <form
          onSubmit={e => e.preventDefault()}
          className="relative min-w-40 max-w-sm flex-1"
        >
          <input
            type="search"
            aria-label="Search jobs"
            placeholder="Search jobs…"
            value={filters.search}
            onChange={e => {
              setFilters({ search: e.target.value });
            }}
            className="input h-7 w-full"
          />
          <LoadingIndicator loading={isLoading} />
        </form>
      </PageHeader>
      <div className="subbar">
        <StatusFilterBar
          counts={statusCounts}
          total={countedTotal}
          stale={countsStale}
          active={filters.status}
          onSelect={next => setFilters({ status: next })}
        />
        {/* Orthogonal to status (a run can be running and waiting), so its
            own toggle rather than a chip in the status group. */}
        <div className="seg" role="group" aria-label="Approval filter">
          <button
            type="button"
            onClick={() => setFilters({ waiting: !filters.waiting })}
            aria-pressed={filters.waiting}
            title="Only runs with samples parked on a human approval"
          >
            Awaiting approval
          </button>
        </div>
      </div>

      <RefreshError error={error} onRetry={refresh} />
      {/* Grid */}
      <div className="flex-1 overflow-hidden relative">
        {/* An empty page while the next one loads is not "no results". */}
        {!hasLoaded || (jobs.length === 0 && isLoading) ? (
          <ListSkeleton columns={['w-48', 'w-32', 'w-24', 'w-32', 'w-16']} />
        ) : jobs.length === 0 ? (
          <div className="p-8 text-center text-sm text-muted">
            <p>
              {filters.waiting
                ? 'No jobs are awaiting an approval.'
                : filters.search
                  ? `No jobs found matching "${filters.search}"`
                  : filters.status
                    ? `No jobs with status “${labelForStatus(filters.status)}”.`
                    : filters.scope === 'mine'
                      ? "You haven't launched any jobs yet."
                      : 'No jobs match your filters.'}
            </p>
            {!filters.waiting &&
              !filters.search &&
              !filters.status &&
              filters.scope === 'mine' && (
                <Link
                  to="/launch"
                  className="btn btn-primary mt-3 no-underline"
                >
                  New eval set
                </Link>
              )}
          </div>
        ) : (
          <div className="ag-theme-quartz rows-open relative h-full w-full">
            <ResetGridLayoutButton layout={layout} />
            <AgGridReact<JobListItem>
              {...layout.gridProps}
              rowData={jobs}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              getRowId={getRowId}
              context={gridContext}
              rowHeight={ROW_HEIGHT}
              onRowClicked={onRowClicked}
              onCellMouseDown={onCellMouseDown}
              animateRows={false}
              suppressCellFocus={true}
              domLayout="normal"
            />
          </div>
        )}
      </div>

      <StopRunDialog
        jobId={stopTarget?.job_id ?? ''}
        open={stopTarget !== null}
        busy={stop.busy}
        error={stop.error}
        onConfirm={kind => void stop.confirm(kind)}
        onClose={() => {
          setStopTarget(null);
          stop.reset();
        }}
      />
      <Pagination
        page={displayPage}
        totalPages={totalPages}
        total={total}
        pageSize={PAGE_SIZE}
        disabled={isLoading}
        onChange={handlePageChange}
      />
    </div>
  );
}
